import { ObjectSchema, boolean, object, string, number, mixed } from 'yup'
import { URLParams } from 'modules/url-params/state/app-url-params-types'
import { EMAIL_VALIDATION_REGEX } from 'utils/constants/misc'
import { Dedication } from './order-dedication-types'

export const makeOrderDedicationInitialState = (urlParams: URLParams): Dedication => {
  return {
    type: 'honor',
    honoreeName: '',
    honoreeFirstName: '',
    honoreeLastName: '',

    recipientName: '',
    recipientFirstName: '',
    recipientLastName: '',
    recipientEmailAddress: '',

    recipientAddress: '',
    recipientCity: '',
    recipientState: '',
    recipientPostalCode: '',
    recipientCountry: '',

    ecardId: undefined,
    ecardMessage: '',

    sendEmail: false,
    addEmailEcard: false,
    sendPrintedCard: false,

    hasDedication: !!urlParams.dedication,
    isGiftAmountMessageIncluded: false,
  }
}

export const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  const trimmedName = fullName.trim()

  if (!trimmedName) {
    return { firstName: '', lastName: '' }
  }

  const [firstName, ...restName] = trimmedName.split(' ')
  const lastName = restName.join(' ')

  return {
    firstName: firstName || '',
    lastName: lastName || '',
  }
}

export const combineNames = (firstName: string, lastName: string): string => {
  return `${firstName || ''} ${lastName || ''}`.trim()
}

export const makeOrderDedicationValidationSchema = (
  t: (key: string, fallback: string) => string,
): ObjectSchema<Dedication> => {
  return object({
    // basic fields
    type: mixed<'honor' | 'memory'>()
      .oneOf(
        ['honor', 'memory'],
        t('orderDedication.validation.type.invalid', 'Invalid dedication type'),
      )
      .required(t('orderDedication.validation.type.required', 'Dedication type is required')),

    honoreeName: string().required(
      t('orderDedication.validation.honoreeName.required', "Honoree's name is required"),
    ),

    honoreeFirstName: string().default(''),

    honoreeLastName: string().default(''),

    // email fields

    recipientName: string()
      .default('')
      .when('sendEmail', {
        is: true,
        then: (schema) =>
          schema.required(
            t(
              'orderDedication.validation.recipientFirstName.required',
              'Recipient name is required',
            ),
          ),
      })
      .when('sendPrintedCard', {
        is: true,
        then: (schema) =>
          schema.required(
            t(
              'orderDedication.validation.recipientFirstName.required',
              'Recipient name is required',
            ),
          ),
      }),

    recipientFirstName: string().default(''),

    recipientLastName: string().default(''),

    recipientEmailAddress: string()
      .default('')
      .when('sendEmail', {
        is: true,
        then: (schema) =>
          schema
            .required(
              t(
                'orderDedication.validation.recipientEmailAddress.required',
                'Recipient email is required',
              ),
            )
            .matches(
              EMAIL_VALIDATION_REGEX,
              t(
                'orderDedication.validation.recipientEmailAddress.invalidFormat',
                'Please enter in the format: name@domain.com.',
              ),
            ),
      }),

    // printed card fields

    recipientAddress: string()
      .default('')
      .when('sendPrintedCard', {
        is: true,
        then: (schema) =>
          schema.required(
            t('orderDedication.validation.recipientAddress.required', 'Address is required'),
          ),
      }),

    recipientCity: string()
      .default('')
      .when('sendPrintedCard', {
        is: true,
        then: (schema) =>
          schema.required(
            t('orderDedication.validation.recipientCity.required', 'City is required'),
          ),
      }),

    recipientState: string()
      .default('')
      .when('sendPrintedCard', {
        is: true,
        then: (schema) =>
          schema.required(
            t('orderDedication.validation.recipientState.required', 'State is required'),
          ),
      }),

    recipientPostalCode: string()
      .default('')
      .when('sendPrintedCard', {
        is: true,
        then: (schema) =>
          schema
            .required(
              t('orderDedication.validation.recipientPostalCode.required', 'Zip code is required'),
            )
            .matches(
              /^[0-9]{5}(?:-[0-9]{4})?$/,
              t('orderDedication.validation.recipientPostalCode.invalid', 'Invalid zip code'),
            ),
      }),

    recipientCountry: string()
      .default('')
      .when('sendPrintedCard', {
        is: true,
        then: (schema) =>
          schema.required(
            t('orderDedication.validation.recipientCountry.required', 'Country is required'),
          ),
      }),

    // ecard fields

    ecardId: number().when('addEmailEcard', {
      is: true,
      then: (schema) =>
        schema.required(t('orderDedication.validation.ecardId.required', 'Please select an eCard')),
      otherwise: (schema) => schema.notRequired(),
    }),

    ecardMessage: string()
      .default('')
      .when('addEmailEcard', {
        is: true,
        then: (schema) =>
          schema.max(
            500,
            t(
              'orderDedication.validation.ecardMessage.maxLength',
              'Message must be 500 characters or less',
            ),
          ),
      }),

    // notification fields

    sendEmail: boolean().default(false),
    addEmailEcard: boolean().default(false),
    sendPrintedCard: boolean().default(false),

    // other fields

    hasDedication: boolean().default(false),
    isGiftAmountMessageIncluded: boolean().default(false),
  })
}
